import { useCallback } from "react";
import * as THREE from "three";
import { getJointPositionSliderScreenPosition } from "../scene/sceneMisc";
import { findJointVizGroupInScene } from "../scene/sceneObjectLookups";
import { getManagedLabelKind } from "../labelUtils";
import { useJointPositionSliderOverlayState } from "./useJointPositionSliderOverlayState";

export function useSceneJointRuntime({
  actuatorSliderState,
  cameraRef,
  isPhysicsRunning,
  jointLookup,
  rendererRef,
  safeJointMapObject,
  sceneActions,
  sceneRef,
  selectedJoint,
  showJointPositionSlider,
  showJoints,
}) {
  const getJointSnapshot = useCallback(
    (identifier) => {
      if (!identifier) return null;
      if (jointLookup.has(identifier)) {
        return jointLookup.get(identifier) || null;
      }
      return null;
    },
    [jointLookup]
  );

  const getJointIdFromObject = useCallback((obj) => {
    // CSS2D joint/site labels are managed by `getManagedLabelKind` visibility rules.
    // Do not treat label nodes as joint marker geometry.
    if (getManagedLabelKind(obj)) return null;
    if (!obj || !obj.userData) return null;
    if (obj.userData.isJointSelectionGlowOnly) return null;
    return obj.userData.jointUuid || null;
  }, []);

  const jointPositionSliderOverlay = useJointPositionSliderOverlayState({
    actuatorSliderState,
    isPhysicsRunning,
    safeJointMapObject,
    sceneActions,
    selectedJoint,
    showJointPositionSlider,
    showJoints,
  });
  const { setScreenPosition: setJointPositionSliderScreenPos } = jointPositionSliderOverlay;

  const positionJointSliderNextToJoint = useCallback(
    (jointUuid) => {
      if (!jointUuid) return;
      const pos = getJointPositionSliderScreenPosition(
        sceneRef.current,
        cameraRef.current,
        rendererRef.current,
        jointUuid
      );
      if (pos) setJointPositionSliderScreenPos(pos);
    },
    [cameraRef, rendererRef, sceneRef, setJointPositionSliderScreenPos]
  );

  const findJointVizGroup = useCallback(
    (jointIdentifier) => findJointVizGroupInScene(sceneRef.current, jointIdentifier),
    [sceneRef]
  );

  const updateJointVizPivotFromRef = useCallback((viz, ref, pos) => {
    if (!viz || !ref || !Array.isArray(pos) || pos.length !== 3) return;
    const basePos = ref.initialPos || ref.bodyGroup?.position || new THREE.Vector3();
    const baseRot = ref.initialRot || ref.bodyGroup?.quaternion || new THREE.Quaternion();
    const jointOffsetParent = new THREE.Vector3(
      Number(pos[0]) || 0,
      Number(pos[1]) || 0,
      Number(pos[2]) || 0
    ).applyQuaternion(baseRot);
    viz.position.copy(basePos.clone().add(jointOffsetParent));
    viz.quaternion.copy(baseRot);
  }, []);

  return {
    findJointVizGroup,
    getJointIdFromObject,
    getJointSnapshot,
    jointPositionSliderOverlay,
    positionJointSliderNextToJoint,
    setJointPositionSliderScreenPos,
    updateJointVizPivotFromRef,
  };
}
